import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Public demo request page. NOT wrapped in ProtectedRoute — pre-signup by design.
// Uses raw axios (not the shared `api` instance) because that one auto-injects a
// token from localStorage and mounts the tenant-slug interceptor — neither of which
// exist for an unauthenticated prospect.
export default function Demo() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    location: "",
    headache: "",
    website: "", // honeypot — hidden below, real users leave empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.business_name.trim() &&
    form.location.trim() &&
    form.headache.trim() &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${BACKEND}/api/leads`, form);
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError("You've submitted several requests recently. Please try again in an hour.");
      } else if (status === 422) {
        setError("Please double-check the form — one of the fields didn't validate.");
      } else {
        setError("Something went wrong. Please try again, or email brett@brettcpayne.com directly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cc-landing min-h-screen" data-testid="demo-page">
      {/* Nav */}
      <div className="max-w-[900px] mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" data-testid="demo-back-home">
          <Logo withWordmark tone="dark" />
        </Link>
        <Link
          to="/login"
          className="cc-btn-ghost text-[13px] px-4 py-2.5"
          data-testid="demo-signin-btn"
        >
          Sign in <ArrowRight size={14} strokeWidth={1.8} />
        </Link>
      </div>

      <div className="max-w-[720px] mx-auto px-6 pt-10 pb-24">
        {!submitted ? (
          <>
            <div className="inline-block text-[11px] uppercase tracking-[.18em] text-[#ffc72c] font-semibold border border-[#ffc72c]/30 rounded-full px-3 py-1 mb-5">
              Book a demo
            </div>
            <h1 className="font-display font-extrabold text-[36px] sm:text-[44px] leading-[1.05] tracking-tight text-white">
              See it running on your franchise.
            </h1>
            <p className="mt-4 text-[15px] text-[#c8d1de] leading-relaxed">
              Tell us a bit about your business and the one thing eating your Mondays. We&rsquo;ll get back to you within one business day.
            </p>

            <form
              onSubmit={submit}
              className="mt-8 space-y-4"
              data-testid="demo-form"
              noValidate
            >
              {/* Honeypot — hidden from real users. Bots that fill every field will
                  populate this; backend silently discards those submissions. */}
              <div aria-hidden style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}>
                <label>Website (leave blank)
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={update("website")}
                    data-testid="demo-honeypot"
                  />
                </label>
              </div>

              <Field label="Name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="demo-name"
                  className="cc-input"
                  autoComplete="name"
                  maxLength={120}
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="demo-email"
                    className="cc-input"
                    autoComplete="email"
                    maxLength={200}
                  />
                </Field>
                <Field label="Phone" hint="Optional">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="demo-phone"
                    className="cc-input"
                    autoComplete="tel"
                    maxLength={40}
                  />
                </Field>
              </div>
              <Field label="Franchise / business name" required>
                <input
                  type="text"
                  value={form.business_name}
                  onChange={update("business_name")}
                  data-testid="demo-business"
                  className="cc-input"
                  autoComplete="organization"
                  maxLength={200}
                />
              </Field>
              <Field label="Location" hint="City, State" required>
                <input
                  type="text"
                  value={form.location}
                  onChange={update("location")}
                  data-testid="demo-location"
                  className="cc-input"
                  placeholder="Springfield, MO"
                  autoComplete="address-level2"
                  maxLength={120}
                />
              </Field>
              <Field label="What&rsquo;s your biggest ops headache right now?" required>
                <textarea
                  value={form.headache}
                  onChange={update("headache")}
                  data-testid="demo-headache"
                  rows={4}
                  className="cc-input resize-y min-h-[110px]"
                  maxLength={2000}
                  placeholder="Cancellations, hand-entering scorecards, chasing techs for KPI numbers, following up on old estimates — whatever&rsquo;s costing you the most time or revenue this quarter."
                />
                <div className="text-[11px] font-mono-data text-[color:var(--cc-navy-muted)] mt-1 text-right">
                  {form.headache.length}/2000
                </div>
              </Field>

              {error && (
                <div
                  data-testid="demo-error"
                  className="text-[13px] text-[#ff9b8f] bg-[#3d1a1a] border border-[#7a3131] rounded-md px-3 py-2.5"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="demo-submit"
                className="cc-btn-primary w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} strokeWidth={1.8} className="animate-spin" /> Sending&hellip;
                  </>
                ) : (
                  <>
                    Send request <ArrowRight size={16} strokeWidth={1.8} />
                  </>
                )}
              </button>
              <p className="text-[11.5px] text-[color:var(--cc-navy-muted)] mt-2">
                We&rsquo;ll only use these details to reply to your demo request. No lists, no spam.
              </p>
            </form>
          </>
        ) : (
          <div className="mt-10" data-testid="demo-success">
            <div className="w-14 h-14 rounded-full bg-[#0e8a68]/20 border border-[#2dd4a7]/40 flex items-center justify-center mb-6">
              <CheckCircle2 size={28} strokeWidth={1.8} className="text-[#2dd4a7]" />
            </div>
            <h1 className="font-display font-extrabold text-[36px] sm:text-[44px] leading-[1.05] tracking-tight text-white">
              Thanks — we&rsquo;ve got it.
            </h1>
            <p className="mt-4 text-[15px] text-[#c8d1de] leading-relaxed max-w-[560px]">
              A member of the CurrentControl team will reach out within one business day to schedule your demo.
              In the meantime, if it&rsquo;s urgent, email{" "}
              <a href="mailto:brett@brettcpayne.com" className="text-[#ffc72c] hover:underline">
                brett@brettcpayne.com
              </a>
              .
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex cc-btn-ghost"
              data-testid="demo-back-to-landing"
            >
              Back to homepage
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span
          className="text-[11.5px] uppercase tracking-[.14em] font-semibold text-[#c8d1de]"
          dangerouslySetInnerHTML={{ __html: label + (required ? " <span style='color:#ef5757'>*</span>" : "") }}
        />
        {hint && <span className="text-[10.5px] text-[color:var(--cc-navy-muted)]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
